import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { 
  Scissors, 
  Sparkles, 
  ChevronRight,
  Star,
  Filter,
  Loader2,
  MapPin,
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import ServiceCardStandard from './ServiceCardStandard';
import ServiceCardPremium from './ServiceCardPremium';
import ServiceCardDual from './ServiceCardDual';
import ServiceCardCompact from './ServiceCardCompact';
import ServicePillMobile from './ServicePillMobile';
import ServiceBookingModal from './ServiceBookingModal';
import ServiceQuoteModal from './ServiceQuoteModal';
import ServiceCardByCategory from './ServiceCardByCategory';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Service type configurations - expanded for all categories
const SERVICE_CONFIGS = {
  'womens-clothing': {
    title: 'Tailoring & Alterations',
    subtitle: 'Find expert tailors for alterations, custom fittings, and repairs',
    icon: Scissors,
    serviceTypes: ['alterations'],
    color: 'from-purple-600 to-pink-600',
    emoji: '👗',
  },
  'mens-clothing': {
    title: 'Men\'s Tailoring',
    subtitle: 'Professional tailoring for suits, shirts, and alterations',
    icon: Scissors,
    serviceTypes: ['alterations'],
    color: 'from-slate-600 to-gray-700',
    emoji: '👔',
  },
  'kids-clothing': {
    title: 'Kids\' Clothing Services',
    subtitle: 'Parent-trusted providers for school uniforms and occasion wear',
    icon: Scissors,
    serviceTypes: ['alterations'],
    color: 'from-blue-500 to-cyan-500',
    emoji: '🧒',
    trustBadge: true,
  },
  'shoes-footwear': {
    title: 'Shoe Repair & Care',
    subtitle: 'Expert shoe repair, cleaning, and restoration services',
    icon: Scissors,
    serviceTypes: ['shoes-footwear'],
    color: 'from-gray-600 to-slate-600',
    emoji: '👟',
  },
  'bags-accessories': {
    title: 'Bag Repair & Restoration',
    subtitle: 'Luxury bag care, repair, and authentication services',
    icon: Sparkles,
    serviceTypes: ['bags-accessories'],
    color: 'from-amber-500 to-orange-500',
    emoji: '👜',
    luxuryBadge: true,
  },
  'jewellery-watches': {
    title: 'Jewellery & Watch Services',
    subtitle: 'Certified repair, resizing, and authentication',
    icon: Sparkles,
    serviceTypes: ['jewellery-watches'],
    color: 'from-yellow-500 to-amber-500',
    emoji: '💍',
    certificationRequired: true,
  },
  'beauty-skincare': {
    title: 'Beauty Services',
    subtitle: 'Local beauty professionals for facials, makeup, and more',
    icon: Sparkles,
    serviceTypes: ['beauty-skincare'],
    color: 'from-pink-500 to-rose-500',
    emoji: '💄',
  },
  'perfumes-cosmetics': {
    title: 'Fragrance & Beauty Consultation',
    subtitle: 'Premium scent profiling and makeup consultation',
    icon: Sparkles,
    serviceTypes: ['perfumes-cosmetics'],
    color: 'from-purple-400 to-pink-400',
    emoji: '🌸',
  },
  'traditional-wear': {
    title: 'Traditional Wear Specialists',
    subtitle: 'Expert tailoring for sarees, uniforms, office wear, and ethnic outfits',
    icon: Sparkles,
    serviceTypes: ['traditional-wear'],
    color: 'from-orange-500 to-red-500',
    emoji: '🧵',
  },
  'laundry-dryclean': {
    title: 'Laundries & Drycleaners',
    subtitle: 'Professional laundry, dry cleaning, and garment care services',
    icon: Sparkles,
    serviceTypes: ['laundry-dryclean'],
    color: 'from-cyan-500 to-teal-500',
    emoji: '🧺',
    hasDelivery: true, // Enable delivery integration
  },
  'delivery-services': {
    title: 'Delivery Services',
    subtitle: 'Pickup and delivery partners for your convenience',
    icon: Sparkles,
    serviceTypes: ['delivery-services'],
    color: 'from-green-500 to-emerald-500',
    emoji: '🚚',
  },
  // Musical Instruments Categories
  'guitars': {
    title: 'Guitar Services',
    subtitle: 'Lessons, repairs, setups, and more for guitarists',
    icon: Sparkles,
    serviceTypes: ['guitars'],
    color: 'from-orange-500 to-amber-600',
    emoji: '🎸',
  },
  'keyboards-pianos': {
    title: 'Piano & Keyboard Services',
    subtitle: 'Lessons, tuning, and repair services',
    icon: Sparkles,
    serviceTypes: ['keyboards-pianos'],
    color: 'from-indigo-500 to-purple-600',
    emoji: '🎹',
  },
  'drums-percussion': {
    title: 'Drum Services',
    subtitle: 'Drum lessons and percussion training',
    icon: Sparkles,
    serviceTypes: ['drums-percussion'],
    color: 'from-red-500 to-orange-600',
    emoji: '🥁',
  },
  'traditional-instruments': {
    title: 'Traditional Music Services',
    subtitle: 'Learn traditional Sri Lankan instruments',
    icon: Sparkles,
    serviceTypes: ['traditional-instruments'],
    color: 'from-amber-600 to-yellow-500',
    emoji: '🪘',
  },
  'string-instruments': {
    title: 'String Instrument Services',
    subtitle: 'Violin, cello, and string lessons',
    icon: Sparkles,
    serviceTypes: ['string-instruments'],
    color: 'from-rose-500 to-pink-600',
    emoji: '🎻',
  },
  'wind-instruments': {
    title: 'Wind Instrument Services',
    subtitle: 'Saxophone, trumpet, flute lessons and repairs',
    icon: Sparkles,
    serviceTypes: ['wind-instruments'],
    color: 'from-sky-500 to-blue-600',
    emoji: '🎺',
  },
  'dj-equipment': {
    title: 'DJ Services',
    subtitle: 'DJ lessons and equipment rental',
    icon: Sparkles,
    serviceTypes: ['dj-equipment'],
    color: 'from-violet-500 to-purple-600',
    emoji: '🎧',
  },
  'amplifiers-effects': {
    title: 'Amp & Effects Services',
    subtitle: 'Amplifier repairs and pedal services',
    icon: Sparkles,
    serviceTypes: ['amplifiers-effects'],
    color: 'from-zinc-600 to-slate-700',
    emoji: '🔊',
  },
  'music-accessories': {
    title: 'Music Accessories Services',
    subtitle: 'Accessories delivery and supplies',
    icon: Sparkles,
    serviceTypes: ['music-accessories'],
    color: 'from-teal-500 to-cyan-600',
    emoji: '🎼',
  },
  'sheet-music': {
    title: 'Music Theory Services',
    subtitle: 'Music theory classes and notation lessons',
    icon: Sparkles,
    serviceTypes: ['sheet-music'],
    color: 'from-emerald-500 to-green-600',
    emoji: '📜',
  },
  'audio-equipment': {
    title: 'Audio Equipment Services',
    subtitle: 'Installation, calibration, and repair for PA systems, monitors, and mixers',
    icon: Sparkles,
    serviceTypes: ['audio-equipment'],
    color: 'from-blue-600 to-indigo-700',
    emoji: '🔊',
  },
  'recording-gear': {
    title: 'Recording Gear Services',
    subtitle: 'Studio setup, mic repair, and recording equipment services',
    icon: Sparkles,
    serviceTypes: ['recording-gear'],
    color: 'from-purple-600 to-violet-700',
    emoji: '🎙️',
  },
  'lessons-tuition': {
    title: 'Music Lessons & Tuition',
    subtitle: 'Learn from certified teachers - piano, guitar, drums, vocals & more',
    icon: Sparkles,
    serviceTypes: ['lessons-tuition'],
    color: 'from-green-500 to-emerald-600',
    emoji: '🎓',
    providerType: 'TEACHER',
  },
  'repairs-servicing': {
    title: 'Repairs & Servicing',
    subtitle: 'Expert instrument repairs, setups, and maintenance',
    icon: Sparkles,
    serviceTypes: ['repairs-servicing'],
    color: 'from-orange-500 to-red-600',
    emoji: '🔧',
    providerType: 'REPAIR_TECH',
  },
  'rentals-hire': {
    title: 'Rentals & Hire',
    subtitle: 'Rent instruments and equipment for events, practice, or recordings',
    icon: Sparkles,
    serviceTypes: ['rentals-hire'],
    color: 'from-cyan-500 to-blue-600',
    emoji: '📦',
    providerType: 'RENTAL_SHOP',
  },
};

/**
 * ServicesSection
 * Main component to display services on category pages
 * Shows relevant service providers based on category context
 * Respects admin configuration for country-specific settings
 */
const ServicesSection = ({ 
  category,
  productContext = null, // Optional: product being viewed for contextual services
  userLocation = null,
  maxProviders = 4,
  viewMode = 'grid', // 'grid' | 'list' | 'compact'
  showMobilePill = true,
  country = 'SL', // Country code
}) => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('relevance');
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [selectedFixedService, setSelectedFixedService] = useState(null);
  const [moduleConfig, setModuleConfig] = useState(null);

  // Get config for this category
  const config = SERVICE_CONFIGS[category] || SERVICE_CONFIGS['womens-clothing'];
  const Icon = config.icon;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // First, check if module is enabled for this country
        const configRes = await axios.get(`${API_URL}/api/services/admin/config?country=${country}`);
        const adminConfig = configRes.data;
        setModuleConfig(adminConfig);
        
        // If module is disabled or category not enabled, don't fetch providers
        if (!adminConfig?.enabled) {
          setProviders([]);
          setLoading(false);
          return;
        }
        
        // Check if current category is enabled
        if (!adminConfig?.categories_enabled?.includes(category)) {
          setProviders([]);
          setLoading(false);
          return;
        }
        
        // Fetch providers
        const params = new URLSearchParams({
          service_type: config.serviceTypes[0],
          sort_by: sortBy,
          limit: (adminConfig?.max_providers_shown || maxProviders).toString(),
          enable_ai_ranking: 'true',
          country: country,
        });
        
        if (userLocation?.lat && userLocation?.lng) {
          params.append('user_lat', userLocation.lat);
          params.append('user_lng', userLocation.lng);
        }
        
        const response = await axios.get(`${API_URL}/api/services/providers?${params}`);
        setProviders(response.data.providers || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching service providers:', err);
        setError('Failed to load services');
        setProviders([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [category, sortBy, userLocation, config.serviceTypes, maxProviders, country]);

  const handleBook = (provider, fixedService = null) => {
    setSelectedProvider(provider);
    setSelectedFixedService(fixedService);
    setShowBookingModal(true);
  };

  const handleRequestQuote = (provider) => {
    setSelectedProvider(provider);
    setShowQuoteModal(true);
  };

  const handleViewDetails = (provider) => {
    // Navigate to provider detail page or open modal
    console.log('View details:', provider);
  };

  const handleBookingSubmit = async (bookingData) => {
    try {
      await axios.post(`${API_URL}/api/services/bookings`, bookingData);
      // Success handling is done in the modal
    } catch (err) {
      console.error('Booking failed:', err);
      throw err;
    }
  };

  const handleQuoteSubmit = async (quoteData, formData) => {
    try {
      await axios.post(`${API_URL}/api/services/quotes`, quoteData);
      // Success handling is done in the modal
    } catch (err) {
      console.error('Quote request failed:', err);
      throw err;
    }
  };

  // Determine card type based on provider characteristics
  const getCardType = (provider) => {
    const hasFixedServices = provider.fixed_price_services?.length > 0;
    const hasQuoteServices = provider.quote_services?.length > 0;
    
    if (provider.verification_level >= 3 || provider.portfolio_images?.length > 0) {
      return 'premium';
    }
    if (hasFixedServices && hasQuoteServices) {
      return 'dual';
    }
    return 'standard';
  };

  // Get the top sponsored provider for mobile pill (only if sponsored is enabled)
  const sponsoredProvider = moduleConfig?.show_sponsored 
    ? providers.find(p => p.is_sponsored) 
    : null;

  // Don't render if module is disabled
  if (!loading && moduleConfig && !moduleConfig.enabled) {
    return null;
  }

  if (loading) {
    return (
      <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
          <span className="ml-2 text-purple-600">Loading services...</span>
        </CardContent>
      </Card>
    );
  }

  if (error || providers.length === 0) {
    return null; // Don't show section if no providers
  }

  // Check if AI badge should be shown
  const showAiBadge = moduleConfig?.show_ai_badge ?? true;

  return (
    <>
      <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200 overflow-hidden" data-testid="services-section">
        {/* Header */}
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <div className={`p-1.5 rounded-lg bg-gradient-to-br ${config.color}`}>
                <Icon className="h-5 w-5 text-white" />
              </div>
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  {config.title}
                  {showAiBadge && (
                    <Badge className="bg-purple-100 text-purple-700 text-xs py-0">
                      <Sparkles className="h-3 w-3 mr-1" />
                      AI Matched
                    </Badge>
                  )}
                </CardTitle>
                <p className="text-xs text-gray-600">{config.subtitle}</p>
              </div>
            </div>
            
            {/* Sort & Filter */}
            <div className="flex items-center gap-2">
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-36 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="relevance">✨ Best Match</SelectItem>
                  <SelectItem value="rating">⭐ Top Rated</SelectItem>
                  <SelectItem value="price">💰 Price</SelectItem>
                  <SelectItem value="distance">📍 Nearest</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="pt-0">
          {/* Providers Grid */}
          <div className={`grid gap-4 ${
            viewMode === 'grid' 
              ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-2' 
              : viewMode === 'compact'
              ? 'grid-cols-1'
              : 'grid-cols-1'
          }`}>
            {providers.map((provider) => {
              const cardType = getCardType(provider);
              
              // Use category-specific cards for all new categories
              const useCategoryCard = [
                'kids-clothing', 'shoes-footwear', 'bags-accessories', 
                'jewellery-watches', 'beauty-skincare', 'perfumes-cosmetics',
                'traditional-wear', 'laundry-dryclean'
              ].includes(category);
              
              if (useCategoryCard) {
                return (
                  <ServiceCardByCategory
                    key={provider.id}
                    provider={provider}
                    category={category}
                    onBook={() => handleBook(provider)}
                    onQuote={() => handleRequestQuote(provider)}
                    isSponsored={provider.is_sponsored}
                  />
                );
              }
              
              if (viewMode === 'compact') {
                return (
                  <ServiceCardCompact
                    key={provider.id}
                    service={provider}
                    onClick={handleViewDetails}
                  />
                );
              }
              
              if (cardType === 'premium') {
                return (
                  <ServiceCardPremium
                    key={provider.id}
                    service={provider}
                    onBook={handleBook}
                    onRequestQuote={handleRequestQuote}
                    onViewDetails={handleViewDetails}
                  />
                );
              }
              
              if (cardType === 'dual') {
                return (
                  <ServiceCardDual
                    key={provider.id}
                    service={provider}
                    onBookFixed={handleBook}
                    onRequestQuote={handleRequestQuote}
                    onViewDetails={handleViewDetails}
                  />
                );
              }
              
              return (
                <ServiceCardStandard
                  key={provider.id}
                  service={provider}
                  onBook={handleBook}
                  onViewDetails={handleViewDetails}
                />
              );
            })}
          </div>
          
          {/* View All Button */}
          {providers.length > 0 && (
            <div className="mt-6 text-center">
              <Button 
                variant="outline" 
                className="border-purple-300 text-purple-700 hover:bg-purple-50"
                data-testid="view-all-services"
                onClick={() => window.location.href = `/services/${category}`}
              >
                View All {config.title}
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Mobile Sticky Pill */}
      {showMobilePill && sponsoredProvider && (
        <ServicePillMobile
          service={sponsoredProvider}
          onClick={handleViewDetails}
        />
      )}

      {/* Booking Modal */}
      <ServiceBookingModal
        open={showBookingModal}
        onClose={() => {
          setShowBookingModal(false);
          setSelectedProvider(null);
          setSelectedFixedService(null);
        }}
        service={selectedProvider}
        selectedFixedService={selectedFixedService}
        onSubmit={handleBookingSubmit}
      />

      {/* Quote Modal */}
      <ServiceQuoteModal
        open={showQuoteModal}
        onClose={() => {
          setShowQuoteModal(false);
          setSelectedProvider(null);
        }}
        service={selectedProvider}
        onSubmit={handleQuoteSubmit}
      />
    </>
  );
};

export default ServicesSection;
